import type {
  ResponsiveValue,
  ShadowProps,
  TypographyProps,
} from 'styled-system';
import { shadow, typography } from 'styled-system';

import type {
  GridItemProps,
  TextDecorationProps,
  TextRenderingProps,
  TextTransformProps,
  TextWrapProps,
  UserSelectProps,
  WhiteSpaceProps,
} from '../extensions';
import {
  gridItem,
  textDecoration,
  textRendering,
  textTransform,
  textWrap,
  userSelect,
  whiteSpace,
} from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';

export interface Props
  extends GridItemProps,
    ShadowProps,
    TextDecorationProps,
    TextRenderingProps,
    TextTransformProps,
    TextWrapProps,
    TypographyProps,
    UserSelectProps,
    WhiteSpaceProps {
  variant?: ResponsiveValue<string>;
  __headingCommon__?: 'common';
  __headingLevelCommon__?: 'common';
}

type Target = 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const createHeadingComponent = (target: Target) => {
  const level = target.replace(/\D+/, '');
  const displayName = level.length > 0 ? `Heading${level}` : 'Heading';
  const variantKey = level.length > 0 ? `h${level}` : undefined;

  const Component = styledFactory<Props, Target>(
    target,
    displayName,
    [
      variantFactory('components.heading', { prop: '__headingCommon__' }),
      variantKey != null &&
        variantFactory(`components.heading.${variantKey}`, {
          prop: '__headingLevelCommon__',
        }),
      variantKey != null &&
        variantFactory(`components.heading.${variantKey}.variants`),
    ].filter(Boolean),
    [
      gridItem,
      shadow,
      textDecoration,
      textRendering,
      textTransform,
      textWrap,
      typography,
      userSelect,
      whiteSpace,
    ],
    undefined,
    {
      variant: variantKey != null ? 'default' : undefined,
      __headingCommon__: 'common',
      __headingLevelCommon__: 'common',
    },
  );

  return Component;
};

/**
 * Components to render styled heading elements.
 */
const Heading = createHeadingComponent('div');

export default Object.assign(Heading, {
  h1: createHeadingComponent('h1'),
  h2: createHeadingComponent('h2'),
  h3: createHeadingComponent('h3'),
  h4: createHeadingComponent('h4'),
  h5: createHeadingComponent('h5'),
  h6: createHeadingComponent('h6'),
});
